import { useEffect, useMemo, useRef } from 'react';
import debounce from 'lodash/debounce';
import { getLastEpisodeLocator, getVisibleReadingLocator } from '../utils/readingProgressUtils';
import type { PageItem } from '../utils/flattenEpisodesToPages';

const DEBOUNCE_MS = 1500;
const DETECT_DELAY_MS = 300;
const IDLE_TIMEOUT_MS = 500;

interface UseReadingProgressFromPageParams {
  currentPageRef: React.RefObject<HTMLDivElement | null>;
  sceneId: string | undefined;
  updateReadingProgress: (params: {
    sceneId: string;
    body: { episode_id: string; paragraph_index: number; char_offset: number };
  }) => void;
  currentIndex: number;
  currentPage: PageItem | null;
}

export const useReadingProgressFromPage = ({
  currentPageRef,
  sceneId,
  updateReadingProgress,
  currentIndex,
  currentPage
}: UseReadingProgressFromPageParams): void => {
  const lastSentKeyRef = useRef<string>('');
  const pendingLocatorRef = useRef<ReturnType<typeof getVisibleReadingLocator>>(null);
  const updateReadingProgressRef = useRef(updateReadingProgress);
  updateReadingProgressRef.current = updateReadingProgress;

  const sendReadingProgress = useMemo(
    () =>
      debounce(
        () => {
          const loc = pendingLocatorRef.current;
          if (!loc || !sceneId) return;
          updateReadingProgressRef.current({
            sceneId,
            body: {
              episode_id: loc.episodeId,
              paragraph_index: loc.paragraphIndex,
              char_offset: loc.charOffset
            }
          });
        },
        DEBOUNCE_MS,
        { leading: false, trailing: true }
      ),
    [sceneId]
  );

  useEffect(
    () => () => {
      sendReadingProgress.cancel();
    },
    [sendReadingProgress]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-detect when page changes; currentPageRef is stable
  useEffect(() => {
    let idleId: number | undefined;

    const detectAndSend = () => {
      const runDetection = () => {
        const isLastPageOfEpisode =
          currentPage &&
          currentPage.pageIndex === currentPage.totalPagesForEpisode - 1;

        let loc = null;
        if (isLastPageOfEpisode) {
          loc = getLastEpisodeLocator(currentPage.episode);
        } else {
          const container = currentPageRef.current;
          if (container) loc = getVisibleReadingLocator(container);
        }

        if (loc) {
          const key = `${loc.episodeId}-${loc.paragraphIndex}-${loc.charOffset}`;
          if (lastSentKeyRef.current !== key) {
            lastSentKeyRef.current = key;
            pendingLocatorRef.current = loc;
            sendReadingProgress();
          }
        }
      };

      if (typeof requestIdleCallback !== 'undefined') {
        idleId = requestIdleCallback(runDetection, { timeout: IDLE_TIMEOUT_MS });
      } else {
        runDetection();
      }
    };

    const timer = setTimeout(detectAndSend, DETECT_DELAY_MS);
    return () => {
      clearTimeout(timer);
      if (idleId !== undefined && typeof cancelIdleCallback !== 'undefined') {
        cancelIdleCallback(idleId);
      }
    };
  }, [currentIndex, currentPage, sendReadingProgress]);
};
